// Singleton KeycastClient from @synvya/auth-identity.
// Module-level forwarders for auth + team management.
// Simplified from client/client — no admin/support endpoints.

import {
  KeycastClient,
  KeycastError,
  isUnauthorized,
  isForbidden,
} from '@synvya/auth-identity'
import type {
  Authorization,
  Team,
  TeamKey,
  TeamRole,
  TeamUser,
  Invitation,
  InviteResponse,
  InvitationPreview,
  AcceptInvitationResponse,
} from '@synvya/auth-identity'
import { getKeycastUrl } from './baseUrl'

export { KeycastError, isUnauthorized, isForbidden }
export type { Authorization, Team, TeamKey, TeamRole, TeamUser, Invitation, InviteResponse, InvitationPreview, AcceptInvitationResponse }

const FETCH_TIMEOUT_MS = 15_000

function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const controller = new AbortController()
  // Honor a user-supplied signal: if the caller aborts, propagate.
  const userSignal = init?.signal
  if (userSignal) {
    if (userSignal.aborted) controller.abort()
    else userSignal.addEventListener('abort', () => controller.abort(), { once: true })
  }
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  return fetch(input, { ...init, signal: controller.signal }).finally(() =>
    clearTimeout(timer)
  )
}

let client: KeycastClient | null = null

function getClient(): KeycastClient {
  if (!client) {
    const baseUrl = getKeycastUrl()
    client = new KeycastClient({
      baseUrl,
      fetch: fetchWithTimeout,
      redirectOrigin: window.location.origin,
    })

    // Warn if app and Keycast are on different registrable domains —
    // SameSite=Lax cookies won't flow cross-site.
    // Common mistake: app on localhost:5173, Keycast on keycast.local.synvya.com:3000
    // (or vice versa). Both must be under *.local.synvya.com in local dev.
    try {
      const keycastHost = new URL(baseUrl).hostname
      const appHost = window.location.hostname
      const keycastIsSynvya = keycastHost.endsWith('.local.synvya.com')
      const appIsSynvya = appHost.endsWith('.local.synvya.com')

      if (keycastIsSynvya && !appIsSynvya) {
        console.error(
          `[keycastClient] COOKIE MISMATCH — Keycast is on ${keycastHost} but app is on ${appHost}.`,
          '\n\nKeycast session cookies (SameSite=Lax) will NOT be sent cross-site.',
          '\nThis causes 401 errors on createTeam, listTeams, and other Keycast API calls.',
          '\n\nFix: open the app at http://account.local.synvya.com:5173 instead of http://localhost:5173.',
          '\nThe vite.config.ts server.host is already set to account.local.synvya.com.',
        )
      } else if (!keycastIsSynvya && appIsSynvya) {
        console.error(
          `[keycastClient] COOKIE MISMATCH — app is on ${appHost} but Keycast is on ${keycastHost}.`,
          '\n\nFix: set VITE_KEYCAST_URL=http://keycast.local.synvya.com:3000 in .env.local',
        )
      } else if (keycastHost === 'localhost' && appHost !== 'localhost') {
        console.warn(
          `[keycastClient] VITE_KEYCAST_URL is ${baseUrl} but app is on ${appHost}.`,
          'Session cookies may not be sent cross-origin.',
          'Set VITE_KEYCAST_URL=http://keycast.local.synvya.com:3000 in .env.local'
        )
      }
    } catch { /* URL parse error — ignore */ }
  }
  return client
}

export const login = (email: string, password: string) =>
  getClient().login(email, password)

export const register = (email: string, password: string) =>
  getClient().register(email, password)

export const logout = () => getClient().logout()

export const listTeams = (): Promise<Team[]> => getClient().listTeams()

export const createTeam = (name: string): Promise<Team> =>
  getClient().createTeam(name)

export const importTeamKey = (
  teamId: number,
  secretKey: string,
  name: string
): Promise<TeamKey> =>
  getClient().importTeamKey(teamId, secretKey, name)

export const createAuthorization = (
  teamId: number,
  pubkey: string,
  policyId: number,
  relays: string[],
  options?: { label?: string }
): Promise<Authorization> =>
  getClient().createAuthorization(teamId, pubkey, policyId, relays, options)

// ---------------------------------------------------------------------------
// Team member management (staff-members page, #16)
// ---------------------------------------------------------------------------

export const addTeamMember = (
  teamId: number,
  userPubkey: string,
  role: TeamRole
): Promise<TeamUser> =>
  getClient().addTeamMember(teamId, userPubkey, role)

export const removeTeamMember = (
  teamId: number,
  userPubkey: string
): Promise<void> =>
  getClient().removeTeamMember(teamId, userPubkey)

export const inviteTeamMember = (
  teamId: number,
  email: string,
  role: TeamRole
): Promise<InviteResponse> =>
  getClient().inviteTeamMember(teamId, email, role)

export const listInvitations = (teamId: number): Promise<Invitation[]> =>
  getClient().listInvitations(teamId)

export const revokeInvitation = (
  teamId: number,
  invitationId: number
): Promise<void> =>
  getClient().revokeInvitation(teamId, invitationId)

// Invitation accept flow (public preview + authenticated accept) — #17

export const previewInvitation = (token: string): Promise<InvitationPreview> =>
  getClient().previewInvitation(token)

export const acceptInvitation = (token: string): Promise<AcceptInvitationResponse> =>
  getClient().acceptInvitation(token)

export const deleteTeam = (teamId: number): Promise<void> =>
  getClient().deleteTeam(teamId)

export const deleteAccount = (): Promise<void> =>
  getClient().deleteAccount()

// ---------------------------------------------------------------------------
// Admin / support-agent endpoints (ported from client/client/src/lib/keycastClient.ts)
// ---------------------------------------------------------------------------

async function adminFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const baseUrl = getKeycastUrl()
  const url = `${baseUrl}${path}`
  const res = await fetchWithTimeout(url, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })

  if (!res.ok) {
    // Read body as text first — Keycast may return non-JSON (HTML 502, plain text)
    const body = await res.text().catch(() => '')
    let message = body || res.statusText
    try {
      const parsed = JSON.parse(body)
      if (typeof parsed.error === 'string') message = parsed.error
    } catch { /* body is not JSON — use as-is */ }
    throw new KeycastError(message, res.status)
  }

  // DELETE /support-access returns 204 No Content — no body to parse
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export interface AdminStatusResponse {
  is_admin: boolean
  role: string | null
}

export interface TeamLookupCandidate {
  id: number
  name: string
  admin_emails: string[]
  has_stored_key: boolean
  created_at: string
}

export interface TeamLookupResponse {
  results: TeamLookupCandidate[]
  total: number
}

export interface GrantSupportAccessResponse {
  team_id: number
  stored_key_pubkey: string
  authorization: Authorization
  bunker_url: string
}

export interface ReleaseSupportAccessResponse {
  team_id: number
  revoked_authorizations: number
  removed_membership: boolean
}

export const adminStatus = (): Promise<AdminStatusResponse> =>
  adminFetch('/api/admin/status')

export const adminTeamLookup = (q: string): Promise<TeamLookupResponse> =>
  adminFetch(`/api/admin/team-lookup?q=${encodeURIComponent(q)}`)

export function grantSupportAccess(
  teamId: number,
  opts?: { policyId?: number; expiresInHours?: number; label?: string },
): Promise<GrantSupportAccessResponse> {
  const body: Record<string, unknown> = {}
  if (opts?.policyId !== undefined) body.policy_id = opts.policyId
  if (opts?.expiresInHours !== undefined) body.expires_in_hours = opts.expiresInHours
  if (opts?.label !== undefined) body.label = opts.label
  return adminFetch(`/api/admin/teams/${teamId}/support-access`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export const releaseSupportAccess = (
  teamId: number,
): Promise<ReleaseSupportAccessResponse> =>
  adminFetch(`/api/admin/teams/${teamId}/support-access`, {
    method: 'DELETE',
  })
